import React, { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { PropTypes } from 'prop-types';
import { MdFormatListBulleted } from 'react-icons/md';
import { DialogsProvider, ToastProvider } from '../../providers';
import { withContextProvider } from '../../hoc';
import EmptyState from '../../componentsV2/EmptyState/EmptyState';
import NoSearchResults from '../../componentsV2/NoSearchResults/NoSearchResults';
import Search from '../../componentsV2/SearchBar/Search';
import tailwindTheme from '../../../../../config/tailwind.config';
import { AppsRounded } from '../../componentsV2/Icons/Icons';
import NotesTable from '../../componentsV2/Notes/NotesTable';
import NoteCards from '../../componentsV2/Notes/NoteCards';
import Pagination from '../../componentsV2/pagination/Pagination';
import useUrlSearchParams from '../../hooks/useUrlSearchParams';
import useFetchNotes from '../../componentsV2/Notes/useFetchNotes';
import NoteViewAddEditModal from '../../componentsV2/Notes/Modals/NoteViewAddEditModal';
import CustomSelect from '../../componentsV2/FormInputs/CustomSelect/CustomSelect';
import { useToasts } from '../../hooks';
import Breadcrumbs from '../../componentsV2/Breadcrumbs/Breadcrumbs';
import PagesHeader from '../../componentsV2/Headers/PagesHeader';
import { updateSearchParamsAndUrl } from '../../utils/updateSearchParamsAndUrl';
import useSyncedSearchParams from '../../hooks/useSyncedSearchParams';
import { parseParams } from '../../utils/parseParams';
import { useUrlModals } from '../../utils/useUrlModals';
import NotFound from '../NotFound/NotFound';
import ResultCount from '../../componentsV2/ResultCount/ResultCount';
import TableSkeletonLoader from '../../componentsV2/LoadingSkeletons/TableSkeletonLoader';
import NoteCardSkeletonLoader from '../../componentsV2/LoadingSkeletons/NoteCardSkeletonLoader';
import ViewModeTooltip from '../../componentsV2/ViewModeTooltip/ViewModeTooltip';

const parseNoteParams = (searchParams) => parseParams(searchParams, {
  site_name: {},
  note_content_title: {},
  status: {},
  priority: {},
  timestamp: {},
  sortBy: {},
  direction: { default: 'desc' },
  perPage: { type: 'int', default: 20 },
  page: { type: 'int', default: 1 },
});

const getNotePage = (searchParams) => parseInt(searchParams.get('page'), 10) || 1;

const NotesPage = ({ organizationId, currentUserId, currentUserRole }) => {
  const [viewType, setViewType] = useUrlSearchParams('mode', 'table');
  const defaultItemsPerPage = viewType === 'table' ? 20 : 24;
  const [sortBy, setSortBy] = useUrlSearchParams('sortBy', ''); // Track sorting parameters in URL
  const [direction, setDirection] = useUrlSearchParams('direction', 'desc'); // Default to 'desc'

  const {
    params, setParams, currentPage, setCurrentPage,
  } = useSyncedSearchParams(parseNoteParams, getNotePage);

  const itemsPerPage = params.perPage || defaultItemsPerPage;
  const [totalNotes, setTotalNotes] = useState(null);
  const [totalUnfilteredNotes, setTotalUnfilteredNotes] = useState(null);
  const { showToast } = useToasts();
  const isLoading = totalNotes === null || totalUnfilteredNotes === null;

  const [notes, fetchNotes] = useFetchNotes(organizationId, params, currentPage, itemsPerPage, setTotalNotes, setTotalUnfilteredNotes, false);
  const currentOffset = (currentPage - 1) * itemsPerPage;
  // Updated to include editMode parameter
  const { modals, openModal, closeModal } = useUrlModals({
    noteId: { isIdParam: true },
    editMode: { isIdParam: false },
  });

  const noteModal = modals.noteId || { isOpen: false, value: null };
  const editModeModal = modals.editMode || { isOpen: false, value: null };

  const handleNotesChange = useCallback(() => {
    fetchNotes();
  }, [fetchNotes]);

  const mappedActiveSearchParams = {
    site_name: params.site_name || '',
    note_content_title: params.note_content_title || '',
    status: params.status || '',
  };

  const breadcrumbLinks = [{ href: '/notes', name: 'Notes' }];
  const searchFields = [
    { label: 'Site Name', name: 'site_name', type: 'text' },
    { label: 'Keyword', name: 'note_content_title', type: 'text' },
    {
      label: 'Status', name: 'status', type: 'dropdown', options: ['Open', 'Closed', 'Review'], shouldShowClearOption: true, clearOptionLabel: 'All', dropdownPlaceholder: 'All',
    },
  ];
  const chipsSettings = {
    site_name: { prefix: 'site', type: 'text' },
    note_content_title: { prefix: 'keyword', type: 'text' },
    status: { prefix: 'note status', type: 'text' },
  };

  const viewModeButtons = [
    { mode: 'table', label: 'Table View', Icon: MdFormatListBulleted },
    { mode: 'gallery', label: 'Card View', Icon: AppsRounded },
  ];

  const handleModeChange = (mode) => {
    const updatedParams = {
      ...params,
      offset: 0,
    };

    setViewType(mode);
    setCurrentPage(1);
    setParams(updatedParams);

    updateSearchParamsAndUrl({
      params: {
        ...updatedParams,
        page: 1,
        mode,
      },
    });
  };

  const handleSort = (field = 'timestamp', newDirection = null) => {
    const finalDirection = newDirection || (params.sortBy === field && params.direction === 'asc' ? 'desc' : 'asc');
    setTotalNotes(null);
    setTotalUnfilteredNotes(null);
    setParams((prev) => ({ ...prev, sortBy: field, direction: finalDirection }));
    setSortBy(field);
    setDirection(finalDirection);
    setCurrentPage(1);
  };

  // eslint-disable-next-line no-nested-ternary
  const getSortValue = () => (sortBy === '' ? 'Sort' : (direction === 'desc' ? 'Newest' : 'Oldest'));

  const handleSearch = (values) => {
    const updatedParams = {
      ...params, ...values, page: 1, perPage: itemsPerPage,
    };

    setTotalNotes(null);
    setTotalUnfilteredNotes(null);

    setParams(updatedParams);
    setCurrentPage(1);
    updateSearchParamsAndUrl({ params: updatedParams });
  };

  const onPageChange = (page) => {
    const updatedParams = { ...params, page, perPage: itemsPerPage };

    setTotalNotes(null);
    setTotalUnfilteredNotes(null);

    setCurrentPage(page);
    setParams(updatedParams);
    updateSearchParamsAndUrl({ params: updatedParams });
  };

  // Function to handle opening edit mode
  const handleOpenEditMode = () => {
    openModal('editMode', 'true');
  };

  // Function to handle closing edit mode
  const handleCloseEditMode = () => {
    closeModal('editMode');
  };

  // Function to handle opening the entire note modal
  const handleOpenNoteModal = useCallback(
    (id) => openModal('noteId', id),
    [openModal],
  );

  // Function to handle closing the entire note modal
  const handleCloseNoteModal = () => {
    closeModal('noteId');
    closeModal('editMode'); // Also close edit mode when closing note modal
  };

  const renderSkeletonLoader = () => {
    if (viewType === 'table') {
      return (
        <div className="flex justify-center w-full">
          <div className="pt-8 w-9/12">
            <TableSkeletonLoader
              numRows={itemsPerPage}
              numColumns={5}
              skeletonHeight={16}
              skeletonWidth="70%"
            />
          </div>
        </div>
      );
    }

    const loaderKeys = Array.from({ length: itemsPerPage }, () => uuidv4());

    return (
      <div className="w-full">
        <div className="grid justify-start grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 3xl:grid-cols-6 gap-x-6 gap-y-8">
          {loaderKeys.map((key) => (
            <NoteCardSkeletonLoader key={key} />
          ))}
        </div>
      </div>
    );
  };

  const renderView = () => (
    viewType === 'table'
      ? (
        <NotesTable
          notes={notes}
          onSort={handleSort}
          onClick={handleOpenNoteModal}
          defaultSortOrder={{ sortBy: params.sortBy || null, order: params.direction || null }}
        />
      )
      : (
        <div className="flex flex-wrap gap-y-8 justify-between">
          <NoteCards
            notes={notes}
            onClick={handleOpenNoteModal}
          />
        </div>
      )
  );

  let renderContent;
  if (isLoading) {
    renderContent = renderSkeletonLoader();
  } else if (notes.length > 0) {
    renderContent = (
      <>
        {renderView()}
        <footer>
          <Pagination
            itemsPerPage={itemsPerPage}
            totalItems={totalNotes}
            currentPage={currentPage}
            setCurrentPage={onPageChange}
            marginTop="my-8"
          />
        </footer>
      </>
    );
  } else if (totalUnfilteredNotes === 0) {
    renderContent = <EmptyState pageType="notes" onAdd={() => {}} />;
  } else {
    renderContent = <NoSearchResults pageType="notes" />;
  }

  const noteId = noteModal?.value;
  const isViewingNote = noteModal?.isOpen && noteId;

  const existingNote = isViewingNote
    ? notes.find((n) => n.id === Number(noteId))
    : null;

  const notFound = isViewingNote && !existingNote;

  if (notFound) {
    return <NotFound />;
  }
  const buildOptions = (array) => array?.map(item => ({
    label: item,
    value: item,
  }));
  const fieldOptions = buildOptions(['Newest', 'Oldest']);

  return (
    <DialogsProvider>
      <div className="ml-2.75 w-full min-h-screen">
        <header>
          <div className="flex justify-between items-start w-full">
            {/* Breadcrumbs */}
            <Breadcrumbs links={breadcrumbLinks} />
            {/* Search Section */}
            <div className="self-start">
              <Search onSearch={handleSearch} fields={searchFields} title="search notes" chipsSettings={chipsSettings} activeSearchParams={mappedActiveSearchParams} />
            </div>
          </div>
        </header>
        <div className="flex w-full">
          {!isLoading && notes.length > 0 && (
            <div className="flex w-full gap-2 justify-between items-center pb-4">
              <PagesHeader name="notes" label="Notes" size="h-9" />
              {totalNotes > 0 && (
                <div className="flex-1 flex justify-center">
                  <ResultCount
                    offset={currentOffset}
                    pageSize={notes.length}
                    total={totalNotes}
                    unfilteredTotal={totalUnfilteredNotes}
                    viewType={viewType}
                    pageType="notes"
                  />
                </div>
              )}
              <div className="flex gap-2 items-center">
                {viewType === 'gallery' && (
                <div className="justify-end">
                  <CustomSelect
                    value={getSortValue()}
                    options={fieldOptions}
                    setValue={(value) => handleSort('timestamp', value.value === 'Newest' ? 'desc' : 'asc')}
                    isValid
                    borderColor="border-vamos-blue-gray"
                    inputWidth="min-w-24"
                    emptyLabel="Sort"
                    disableEmptyOption={sortBy !== ''}
                    inputPadding="0"
                  />
                </div>

                )}
                <div className="flex gap-2 items-center">
                  {viewModeButtons.map(({ mode, label, Icon }) => (
                    <ViewModeTooltip
                      key={mode}
                      id={`notes-${mode}-view-tooltip`}
                      label={label}
                    >
                      <button
                        type="button"
                        onClick={() => handleModeChange(mode)}
                        aria-label={label}
                        className={`flex justify-center items-center w-8 h-8 rounded-md border-none bg-${viewType === mode ? 'vamos-green1' : 'vamos-white'} focus:outline-none`}
                      >
                        <Icon
                          size={24}
                          color={`${viewType === mode ? tailwindTheme.theme.extend.colors['vamos-white'] : tailwindTheme.theme.extend.colors['vamos-black']}`}
                          className={`flex items-center justify-center rounded-md bg-${viewType === mode ? 'vamos-green1' : 'vamos-white'}`}
                        />
                      </button>
                    </ViewModeTooltip>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        {renderContent}
        {noteModal.isOpen && (
          <NoteViewAddEditModal
            activeNoteId={noteModal.value}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            organizationId={organizationId}
            setOpenAddEditModal={handleCloseNoteModal}
            showToast={showToast}
            onNotesChange={handleNotesChange}
            // Pass edit mode props
            isEditMode={editModeModal.isOpen}
            onOpenEditMode={handleOpenEditMode}
            onCloseEditMode={handleCloseEditMode}
          />
        )}
      </div>
    </DialogsProvider>
  );
};

NotesPage.propTypes = {
  organizationId: PropTypes.number.isRequired,
  currentUserId: PropTypes.number.isRequired,
  currentUserRole: PropTypes.string.isRequired,
};

export default withContextProvider(ToastProvider)(NotesPage);
